import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import * as yup from 'yup';
import { useFormik } from 'formik';

import MenuItem from '@mui/material/MenuItem';

const CreateEmailNotification = (props) => {
    const { is_email_notification_modal_open,
        handleEmailNotificationModal,
        is_loading,
        handleEmailNotificationSubmit,
        handleEmailNotificationUpdate,
        c_action,
        email_notification_id,
        selected_data,
        stage_list,
        company_list,
        process_list
    } = props;
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleEmailNotificationCloseModalChange = () => {
        formik.resetForm();
        handleEmailNotificationModal(false);
    };

    const validationSchema = yup.object().shape({
        stage_id: yup.number().required('Stage ID is required.'),
        company_id: yup.number().required('Company ID is required.'),
        process_id: yup.number().required('Process ID is required.'),
        sent_to: yup.string().required('Sent To is required.'),
        cc: yup.string().nullable().notRequired(),
        subject: yup.string().required('Subject is required.'),
        body: yup.string().optional(),
    });

    const onSubmit = async (values) => {
        let res_create, res_update;

        if (c_action === "UPDATE") {
            const payload = { email_notification_id, updated_data: values };
            res_update = await handleEmailNotificationUpdate(payload);
        } else {
            res_create = await handleEmailNotificationSubmit(values);
        }

        if (
            (res_create || res_update) &&
            !(res_create?.error || res_update?.error)
        ) {
            formik.resetForm();
            handleEmailNotificationModal(false);
            window.location.reload()
        }
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            stage_id: c_action === "UPDATE" ? selected_data.stage_id : '',
            company_id: c_action === "UPDATE" ? selected_data.company_id : '',
            process_id: c_action === "UPDATE" ? selected_data.process_id : '',
            sent_to: c_action === "UPDATE" ? selected_data.sent_to : '',
            cc: c_action === "UPDATE" ? selected_data.cc : '',
            subject: c_action === "UPDATE" ? selected_data.subject : '',
            body: c_action === "UPDATE" ? selected_data.body : '',
        },
        validationSchema: validationSchema,
        onSubmit,
    });

    const getHelperText = (touched, error) => {
        if (touched && typeof error === 'string') {
            return error;
        }
        return '';
    };

    return (
        <Dialog
            fullScreen={fullScreen}
            open={is_email_notification_modal_open}
            onClose={handleEmailNotificationCloseModalChange}
            aria-labelledby="responsive-dialog-title"
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
                '& .MuiDialog-paper': {
                    width: '600px', // Increased width
                    maxWidth: 'md'
                },
            }}
        >
            <DialogTitle
                id="responsive-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {(c_action === 'UPDATE' && 'Update Email Notification') || (c_action === 'CREATE' && 'Create Email Notification')}
                <IconButton size="medium" onClick={handleEmailNotificationCloseModalChange}>
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    '& .MuiTextField-root,.MuiFormControl-root': {
                        width: '100%', // Use full width of grid cell
                        fontSize: '14px',
                    },
                    padding: '24px' // Add some padding
                }}
            >
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, // Single col on mobile, two cols on tablet+
                    gap: 2,
                    marginTop: '6px'
                }}>
                    <TextField
                        select
                        label="Stage"
                        variant="outlined"
                        size="small"
                        name="stage_id"
                        value={formik.values.stage_id}
                        onChange={formik.handleChange('stage_id')}
                        onBlur={formik.handleBlur('stage_id')}
                        error={formik.touched.stage_id && Boolean(formik.errors.stage_id)}
                        helperText={getHelperText(formik.touched.stage_id, formik.errors.stage_id)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    >
                        {Array.isArray(stage_list) ? stage_list.map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.stage_name || option?.name || ''}
                            </MenuItem>
                        )) : (stage_list?.data?.stage || []).map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.stage_name || option?.name || ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Company"
                        variant="outlined"
                        size="small"
                        name="company_id"
                        value={formik.values.company_id}
                        onChange={formik.handleChange('company_id')}
                        onBlur={formik.handleBlur('company_id')}
                        error={formik.touched.company_id && Boolean(formik.errors.company_id)}
                        helperText={getHelperText(formik.touched.company_id, formik.errors.company_id)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    >
                        {Array.isArray(company_list) ? company_list.map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.name || option?.company_name || ''}
                            </MenuItem>
                        )) : (company_list?.data?.company || []).map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.name || option?.company_name || ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label="Process"
                        variant="outlined"
                        size="small"
                        name="process_id"
                        value={formik.values.process_id}
                        onChange={formik.handleChange('process_id')}
                        onBlur={formik.handleBlur('process_id')}
                        error={formik.touched.process_id && Boolean(formik.errors.process_id)}
                        helperText={getHelperText(formik.touched.process_id, formik.errors.process_id)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    >
                        {Array.isArray(process_list) ? process_list.map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.process_name || option?.name || ''}
                            </MenuItem>
                        )) : (process_list?.data?.process || []).map((option, index) => (
                            <MenuItem key={option?.id || index} value={option?.id}>
                                {option?.process_name || option?.name || ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Sent To"
                        variant="outlined"
                        size="small"
                        name="sent_to"
                        value={formik.values.sent_to}
                        onChange={formik.handleChange('sent_to')}
                        onBlur={formik.handleBlur('sent_to')}
                        error={formik.touched.sent_to && Boolean(formik.errors.sent_to)}
                        helperText={getHelperText(formik.touched.sent_to, formik.errors.sent_to)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    />

                    <TextField
                        label="CC"
                        variant="outlined"
                        size="small"
                        name="cc"
                        value={formik.values.cc}
                        onChange={formik.handleChange('cc')}
                        onBlur={formik.handleBlur('cc')}
                        error={formik.touched.cc && Boolean(formik.errors.cc)}
                        helperText={getHelperText(formik.touched.cc, formik.errors.cc)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    />

                    <TextField
                        label="Subject"
                        variant="outlined"
                        size="small"
                        name="subject"
                        value={formik.values.subject}
                        onChange={formik.handleChange('subject')}
                        onBlur={formik.handleBlur('subject')}
                        error={formik.touched.subject && Boolean(formik.errors.subject)}
                        helperText={getHelperText(formik.touched.subject, formik.errors.subject)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                    />


                    <TextField
                        label="Body"
                        variant="outlined"
                        size="small"
                        name="body"
                        multiline
                        rows={4}
                        value={formik.values.body}
                        onChange={formik.handleChange('body')}
                        onBlur={formik.handleBlur('body')}
                        error={formik.touched.body && Boolean(formik.errors.body)}
                        helperText={getHelperText(formik.touched.body, formik.errors.body)}
                        sx={{ fontSize: { xs: '12px', sm: '14px' }, gridColumn: { sm: '1 / -1' } }} // Full width
                    />
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    padding: { xs: '12px', sm: '8px 16px 24px 24px' },
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: '100%'
                }}
            >
                <Button
                    variant="outlined"
                    color="error"
                    onClick={handleEmailNotificationCloseModalChange}
                    sx={{
                        fontSize: { xs: '12px', sm: '14px' },
                        borderColor: "#ff6100", color: "#4c4e52"
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    sx={{
                        fontSize: { xs: '12px', sm: '14px' },
                        backgroundColor: "#ff6100"
                    }}
                >
                    {(c_action === 'UPDATE' && 'Update') || (c_action === 'CREATE' && 'Submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateEmailNotification;
